import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import { isDirty as isFormDirty } from 'redux-form'
import { History } from 'history'
import classnames from 'classnames'
import PlateVinForm from './plateVinForm'
import VehicleDetailFormSanitizer from '../../common/vehicles/vehicleDetailFormSanitizer'
import LoaderButton from '../../../components/loaderButton'
import LoadingSection from '../../../components/loadingSection'
import { VehicleError } from './vehicleError'
import {
  selectCurrentRefiVehicle,
  selectRefiAppVehicleUiState,
  selectRefiAppProfileUiState,
  selectCurrentRefiApplication,
  selectCurrentRefiUser,
  selectCurrentProductPackageId,
  selectProductPackage,
} from '../../../reducers'
import {
  findOrCreateVehicle,
  deleteCurrentApplicationVehicle,
  setEditVehicleDetailsTrue,
  updateVehicle,
  toggleVehicleUiState,
  openProfileSection,
  openVehicleSection,
  openCoverageSection,
  setRefiAppRejectedCreditAppState,
  getRefi,
  continueRefi,
  triggerVinRequiredError,
} from '../refiActions'
import { Vehicle, User, ProductPackage } from '../../../core/types'
import { parseQuery } from '../../../helpers/queryHelper'
import { fetchProductPackage } from '../../products/productActions'
import { onContinueWithoutCredit } from '../credit/creditApplicationChoice'

export type Callback<T = void> = (...args: any[]) => T

const PROFILE_FORM_NAME = 'ProfileDetailForm'

const hasFormChanges = (state, formName: string) => {
  return isFormDirty(formName)(state) && !!state?.form?.[formName]?.anyTouched
}

/**
 * True when we have an email for the customer but no "Re-enter email" to
 * confirm it with. CallTools contact syncs land in
 * crmSyncMetadata.unverified_email, which prefills Email but not Re-enter
 * email, so the agent still has to confirm the address before we can move on.
 */
export const selectNeedsEmailConfirmation = (state) => {
  const user = selectCurrentRefiUser(state) || ({} as User)
  const values = state?.form?.[PROFILE_FORM_NAME]?.values
  const knownEmail = user.email || user.crmSyncMetadata?.unverified_email || ''

  const generateEmail = values ? values.generateEmail === true : !knownEmail
  if (generateEmail) return false

  const email = (values ? values.email : knownEmail) || ''
  const email2 = (values ? values.email2 : user.email || '') || ''

  return !!email.trim() && !email2.trim()
}

const getNamedForm = (formName: string) => {
  return document.querySelector(
    `form[name="${formName}"]`
  ) as HTMLFormElement | null
}

const getAutosaveMetadata = (form: HTMLFormElement | null) => ({
  isAutosave: form?.getAttribute('data-autosave') === 'true',
  autosaveTarget: form?.getAttribute('data-autosave-target'),
})

const clearAutosaveMetadata = (form: HTMLFormElement | null) => {
  form?.setAttribute('data-autosave', 'false')
  form?.setAttribute('data-autosave-target', '')
}

const submitFormByName = (
  formName: string,
  autosaveTarget: 'vehicle' | 'profile' | 'coverage'
) => {
  const form = getNamedForm(formName)

  if (!form) return

  form.setAttribute('data-autosave', 'true')
  form.setAttribute('data-autosave-target', autosaveTarget)

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit()
    return
  }

  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
}

const ResetButton = ({ deleteCurrentApplicationVehicle }) => (
  <button
    className="button button-drive-america"
    onClick={() => deleteCurrentApplicationVehicle()}
    data-testid="refiApplicationClearLink"
  >
    Start over
  </button>
)

const EditButton = ({ setEditVehicleDetailsTrue }) => (
  <button
    className="button button-drive-america button-drive-america--secondary margin-right margin-top"
    onClick={() => setEditVehicleDetailsTrue()}
    data-testid="refiApplicationEditDetailsLink"
  >
    Edit details
  </button>
)

const vehicleDetailControls = (submitting) => (
  <div className="form__group">
    <LoaderButton
      type="submit"
      className="button button-drive-america"
      isLoading={submitting}
    >
      Next
    </LoaderButton>
  </div>
)

export const Section = ({
  uiStatus,
  vehicle,
  editVehicleDetails,
  findOrCreateVehicle,
  setEditVehicleDetailsTrue,
  deleteCurrentApplicationVehicle,
  openProfileSection,
  openVehicleSection,
  userId,
  user,
  productPackage,
  needsEmailConfirmation,
  history,
}) => {
  const productPackageStatus =
    productPackage?.status?.code || productPackage?.status

  const dispatch = useDispatch()

  const onPlateVinSubmit = (values) => {
    const plateVinForm = getNamedForm('PlateVinForm')
    const { isAutosave, autosaveTarget } = getAutosaveMetadata(plateVinForm)

    const trimId =
      typeof values.trim_id === 'number'
        ? values.trim_id
        : values.trim_id && values.trim_id.trim()

    // ensure blank VIN is sent as null
    const payload = {
      ...values,
      id: vehicle.id,
      vin: values.vin && values.vin.trim() !== '' ? values.vin.trim() : null,
      trim_id: trimId,
      user_id: userId,
      product_package_id: productPackage?.id,
    }
    return findOrCreateVehicle(payload)
      .then((vehicleResp) => {
        // if the user has returned to enter the VIN to validate the rates
        // (package status will be "paid" or "booked"),
        // then update the product package and get the new rates,
        // then proceed to select a new rate or a confirmation screen
        if (
          ['quoted', 'selected', 'booked', 'payment_succeeded'].includes(
            productPackageStatus
          ) ||
          ([undefined, 'empty'].includes(productPackageStatus) &&
            (vehicle?.selected_trim || vehicle?.selectedTrim))
        ) {
          // an unconfirmed email still needs a Re-enter email, so stop at
          // Personal Details instead of skipping ahead to Coverage details
          if (needsEmailConfirmation) {
            openProfileSection()
            return vehicleResp
          }

          return (dispatch as any)(
            onContinueWithoutCredit(
              user,
              productPackage.id,
              history,
              vehicleResp,
              undefined
            )
          ).then(() => vehicleResp)
        }

        // advance wizard: close vehicle and open Personal Details
        openProfileSection()
        return vehicleResp
      })
      .then((vehicleResp) => {
        if (!isAutosave) {
          return vehicleResp
        }

        if (autosaveTarget === 'vehicle') {
          openVehicleSection()
        } else if (autosaveTarget === 'coverage') {
          dispatch(openCoverageSection())
        } else {
          openProfileSection()
        }

        return vehicleResp
      })
      .finally(() => {
        clearAutosaveMetadata(plateVinForm)
      })
  }

  if (!uiStatus) return null

  if (vehicle.refinanceable || editVehicleDetails) {
    // Note: disabling this 2nd form part for MVP
    let disabled = true
    if (!disabled) {
      return (
        <div>
          {!editVehicleDetails && (
            <p>
              Great, we found a car. Let's add more details to see if it
              qualifies for coverage.
            </p>
          )}
          <VehicleDetailFormSanitizer
            vehicle={vehicle}
            controls={vehicleDetailControls}
          />
        </div>
      )
    }
  }

  if (vehicle.id && !vehicle.refinanceable) {
    return (
      <div>
        <VehicleError vehicle={vehicle} />
        <EditButton setEditVehicleDetailsTrue={setEditVehicleDetailsTrue} />
        <ResetButton
          deleteCurrentApplicationVehicle={deleteCurrentApplicationVehicle}
        />
      </div>
    )
  }

  return (
    <PlateVinForm
      onSubmit={onPlateVinSubmit}
      productPackageStatus={productPackageStatus}
    />
  )
}

export interface Props {
  findOrCreateVehicle: Callback
  vehicle: Vehicle
  deleteCurrentApplicationVehicle: Callback
  setEditVehicleDetailsTrue: Callback
  toggleVehicleUiState: Callback
  /** action to open Profile Details section */
  openProfileSection: Callback
  openVehicleSection: Callback
  uiStatus: boolean
  editVehicleDetails: boolean
  setRefiAppRejectedCreditAppState: Callback
  getRefi: Callback
  continueRefi: Callback
  fetchProductPackage: Callback
  triggerVinRequiredError: Callback
  user: User
  productPackage: ProductPackage
  profileUiStatus: boolean
  profileFormChanged: boolean
  needsEmailConfirmation: boolean
  history?: History
}

export const VehicleDetailSection = ({
  vehicle,
  deleteCurrentApplicationVehicle,
  setEditVehicleDetailsTrue,
  editVehicleDetails,
  toggleVehicleUiState,
  openProfileSection,
  openVehicleSection,
  uiStatus,
  findOrCreateVehicle,
  fetchProductPackage,
  setRefiAppRejectedCreditAppState,
  getRefi,
  continueRefi,
  userId,
  triggerVinRequiredError,
  user,
  history,
  productPackage,
  profileUiStatus,
  profileFormChanged,
  needsEmailConfirmation,
}) => {
  const [loading, setLoading] = useState(true)

  const buttonClasses = classnames('refi-app__section-header__button', {
    'refi-app__section-header__button--open': uiStatus,
  })

  const sectionClass = classnames('', {
    'refi-app__section--open': uiStatus,
  })
  const { packageId, refiId } = parseQuery(location.search)

  const fetchRefi = () => {
    //TODO: not found, error handling and loading modal
    return getRefi(refiId).then((refiData) => {
      continueRefi(refiData.payload)
    })
  }

  useEffect(() => {
    if (!packageId && !refiId) {
      // no fetch needed
      setLoading(false)
      return
    }
    let loaderPromise = packageId
      ? fetchProductPackage(packageId).then((pkg) => {
          if (pkg?.status === 'paid' || pkg?.status === 'booked') {
            triggerVinRequiredError()
            openVehicleSection()
          } else {
            setRefiAppRejectedCreditAppState(true)
            // on an empty package, resume at Vehicle Details; otherwise leave Coverage open
            if (!pkg?.products?.length) {
              openVehicleSection()
            }
          }
        })
      : fetchRefi().then(() => {
          // refi loaded
        })

    if (loaderPromise) {
      loaderPromise.finally(() => setLoading(false))
    }

    return () => {
      loaderPromise = null
    }
  }, [packageId, refiId])

  const handleSectionToggle = () => {
    if (!uiStatus) {
      if (profileUiStatus && profileFormChanged) {
        submitFormByName('CreateUserForm', 'vehicle')
      }
    }

    toggleVehicleUiState()
  }

  return (
    <div className={sectionClass}>
      <div
        className="refi-app__section-header"
        onClick={handleSectionToggle}
        data-testid="refiVehicleToggle"
      >
        <h2>Vehicle details</h2>
        <button className={buttonClasses}>Toggle</button>
        <hr className="form__hr" />
      </div>
      <LoadingSection isLoading={loading}>
        <Section
          uiStatus={uiStatus}
          vehicle={vehicle}
          deleteCurrentApplicationVehicle={deleteCurrentApplicationVehicle}
          setEditVehicleDetailsTrue={setEditVehicleDetailsTrue}
          editVehicleDetails={editVehicleDetails}
          findOrCreateVehicle={findOrCreateVehicle}
          openProfileSection={openProfileSection}
          openVehicleSection={openVehicleSection}
          userId={userId}
          user={user}
          productPackage={productPackage}
          needsEmailConfirmation={needsEmailConfirmation}
          history={history}
        />
      </LoadingSection>
    </div>
  )
}

export const mapStateToProps = (state) => {
  const refiApp = selectCurrentRefiApplication(state)

  return {
    vehicle: selectCurrentRefiVehicle(state),
    uiStatus: selectRefiAppVehicleUiState(state),
    editVehicleDetails: !!refiApp.editVehicleDetails,
    userId: refiApp.userId,
    user: selectCurrentRefiUser(state),
    productPackage: selectProductPackage(
      state.productPackages,
      selectCurrentProductPackageId(state)
    ),
    profileUiStatus: selectRefiAppProfileUiState(state),
    profileFormChanged: hasFormChanges(state, PROFILE_FORM_NAME),
    needsEmailConfirmation: selectNeedsEmailConfirmation(state),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    findOrCreateVehicle,
    getRefi,
    continueRefi,
    deleteCurrentApplicationVehicle,
    setEditVehicleDetailsTrue,
    updateVehicle,
    toggleVehicleUiState,
    openProfileSection,
    openVehicleSection,
    fetchProductPackage,
    setRefiAppRejectedCreditAppState,
    triggerVinRequiredError,
  })(VehicleDetailSection)
)
